/**
 * TanStack Router config for TMA — single-file route tree.
 * Routes:
 *   /            → Today (default)
 *   /onboarding  → Onboarding
 *   /chart       → Natal chart
 *   /transits    → Transits week-view
 *   /lunar       → Lunar calendar
 *   /paywall     → Premium paywall
 *   /ai          → AI assistant chat
 */
import { useQuery } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { type ReactElement, useEffect } from "react";

import { fetchMe, type MeProfileResponse, MOCK_MODE } from "@/lib/api";
import { shouldRedirectToOnboarding } from "@/lib/onboarding-gate";
import AIChatPage from "@/pages/AIChat";
import ChartPage from "@/pages/Chart";
import LunarPage from "@/pages/Lunar";
import OnboardingPage from "@/pages/Onboarding";
import PaywallPage from "@/pages/Paywall";
import TodayPage from "@/pages/Today";
import TransitsPage from "@/pages/Transits";
import { useTgContext } from "@/state/tg";

/**
 * Root layout — hosts the onboarding gate so the check runs once for the whole
 * app instead of being re-implemented (and forgotten) per screen.
 *
 * The profile query is shared with the rest of the app through the `["me", …]`
 * query key, so this costs one `GET /users/me` per session, not one per route.
 */
function RootLayout(): ReactElement {
  const tg = useTgContext();
  const navigate = useNavigate();
  const location = useLocation();

  const { data } = useQuery<MeProfileResponse>({
    queryKey: ["me", tg.initData ?? "guest"],
    queryFn: () => fetchMe(tg.initData),
    // No session possible without initData, and mock preview runs on fixtures.
    enabled: !MOCK_MODE && tg.initData !== null,
    staleTime: 5 * 60_000,
    // One attempt: a failed profile read must not delay the gate decision, and
    // the decision on an unknown profile is "don't redirect" anyway.
    retry: false,
  });

  const redirect = shouldRedirectToOnboarding({
    pathname: location.pathname,
    onboarded: data?.onboarded ?? null,
    hasInitData: tg.initData !== null,
    mockMode: MOCK_MODE,
  });

  useEffect(() => {
    if (redirect) void navigate({ to: "/onboarding", replace: true });
  }, [redirect, navigate]);

  return <Outlet />;
}

const rootRoute = createRootRoute({ component: RootLayout });

const todayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: TodayPage,
});
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: OnboardingPage,
});
const chartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chart",
  component: ChartPage,
});
const transitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/transits",
  component: TransitsPage,
});
const lunarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lunar",
  component: LunarPage,
});
const paywallRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/paywall",
  component: PaywallPage,
});
const aiRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ai",
  component: AIChatPage,
});

const routeTree = rootRoute.addChildren([
  todayRoute,
  onboardingRoute,
  chartRoute,
  transitsRoute,
  lunarRoute,
  paywallRoute,
  aiRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
